import type { Match } from "navigo";
import type { ChildDom } from "vanjs-core";
import { reactive } from "vanjs-ext";
import { modal } from "..";
import { getPrices } from "../api/prices";
import { PriceWrapper } from "../billing/prices";
import { locale, t } from "../locale";
import type { Route, RouteContext } from "../router";
import { state } from "../state";
import { userPlaceholder } from "../user/user";
import { deviceSettingsBox } from "../van/device-settings-box";
import { materialSymbol } from "../van/material-symbol";
import { onOffSelect } from "../van/on-off-select";
import {
	button,
	div,
	input,
	label,
	option,
	select,
	table,
	tbody,
	td,
	tr,
} from "../van/van-tags";
import { type Smartplug2, sp2Placeholder, type TimerRule } from "./sp2";

export class Sp2SettingsPage implements Route {
	#approveFota(): void {
		this.#reactive.sp2.Fota.Approved = true;
		this.#save();
	}

	#openCreateTimerRuleModal(): void {
		const days = [false, false, false, false, false, false, false];
		let action = "turn_on";
		let time = "00:00";

		const createRule = (): void => {
			const [hour, minute] = time.split(":");
			const dow = days
				.map((day, i) => (day ? i : -1))
				.filter((i) => i > -1)
				.join(",");
			const newRule = {
				Action: action,
				Schedule: `${+minute} ${+hour} ${dow}`,
				Timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			};
			this.#reactive.sp2.Timer.Rules = [
				...this.#reactive.sp2.Timer.Rules,
				newRule,
			];
			modal.close();
		};

		modal.open({
			header: t("sp2SettingsPage.createTimerRuleModal.header")(),
			body: [
				div(
					{ class: "field is-grouped" },
					div(
						{ class: "field" },
						label(
							{ class: "label" },
							t(
								"sp2SettingsPage.createTimerRuleModal.actionLabel",
							),
						),
						div(
							{ class: "control" },
							div(
								{ class: "select" },
								select(
									{
										onchange: (e: Event) => {
											if (
												!(
													e.target instanceof
													HTMLSelectElement
												)
											)
												return;
											action = e.target.value;
										},
									},
									option(
										{
											value: "turn_on",
										},
										t(
											"sp2SettingsPage.createTimerRuleModal.actionOptions.turnOn",
										),
									),
									option(
										{
											value: "turn_off",
										},
										t(
											"sp2SettingsPage.createTimerRuleModal.actionOptions.turnOff",
										),
									),
								),
							),
						),
					),
					div(
						{ class: "field" },
						label(
							{ class: "label" },
							t("sp2SettingsPage.createTimerRuleModal.timeLabel"),
						),
						div(
							{ class: "control" },
							input({
								class: "input",
								oninput: (e: Event) => {
									if (!(e.target instanceof HTMLInputElement))
										return;
									time = e.target.value;
								},
								type: "time",
								value: time,
							}),
						),
					),
				),
				div(
					{ class: "field" },
					label(
						{ class: "label" },
						t("sp2SettingsPage.createTimerRuleModal.daysLabel"),
					),
					div(
						{ class: "control" },
						div(
							{ class: "checkboxes" },
							locale.weekdays().map((_day, i, weekdays) =>
								label(
									{ class: "checkbox" },
									input({
										type: "checkbox",
										checked: days[i],
										onchange: (e: Event) => {
											if (
												!(
													e.target instanceof
													HTMLInputElement
												)
											)
												return;
											days[(i + 1) % 7] =
												e.target.checked;
										},
									}),
									` ${weekdays[(i + 1) % 7].substring(0, 2)}`,
								),
							),
						),
					),
				),
			],
			footer: [
				button(
					{ class: "button is-primary", onclick: createRule },
					t("sp2SettingsPage.createTimerRuleModal.createButton"),
				),
				button(
					{ class: "button", onclick: modal.close },
					t("sp2SettingsPage.createTimerRuleModal.cancelButton"),
				),
			],
		});
	}

	async #resetPowerConsumption(): Promise<void> {
		this.#reactive.sp2.Consumption = 0;
		this.#save;
	}

	async #save(): Promise<void> {
		this.#reactive.helpText = "";

		const req = this.#ctx.api.put(
			`/smartplug2s/${this.#reactive.sp2.Imei}`,
		);
		req.setJsonBody(this.#reactive.sp2);
		req.setSignedInUserAuth();

		const res = await this.#ctx.loadingScreen.waitFor(req.do());
		if (res.ok) {
			this.#reactive.sp2 = await res.json();
			this.#reactive.helpText = "Ok";
		} else {
			this.#reactive.helpText = await res.text();
		}
	}

	async #unregister(): Promise<void> {
		this.#reactive.sp2.Metadata.OwnerEmail = "";
		this.#save();
		this.#ctx.router.jump("/devices");
	}

	async refresh(match: Match): Promise<void> {
		if (!match?.data?.imei) throw "missing path param: imei";

		this.#reactive.helpText = "";
		this.#reactive.activeUser = await state.getActiveUser();
		this.#reactive.signedInUser = await state.getSignedInUser();
		const { Smartplug2Month, Smartplug2Year } = await getPrices();
		this.#reactive.prices = {
			[Smartplug2Month.Id]: new PriceWrapper(
				this.#reactive.activeUser.Country,
				Smartplug2Month,
			),
			[Smartplug2Year.Id]: new PriceWrapper(
				this.#reactive.activeUser.Country,
				Smartplug2Year,
			),
			"": new PriceWrapper(this.#reactive.activeUser.Country),
		};

		const req = this.#ctx.api.get(`/smartplug2s/${match.data.imei}`);
		req.setSignedInUserAuth();

		const res = await this.#ctx.loadingScreen.waitFor(req.do());
		if (res.ok) {
			const sp2 = (await res.json()) as Smartplug2;
			this.#reactive.sp2 = sp2;
			this.#reactive.isOwner =
				sp2.Metadata.OwnerEmail === this.#reactive.signedInUser.Email;
			this.#reactive.hasActiveSubscription =
				sp2.Subscription.Trialing ||
				!!sp2.Subscription.PriceIds.Current;
		}
	}

	#consumptionField: ChildDom = () =>
		div(
			{ class: "field" },
			label(
				{ class: "label" },
				t("sp2SettingsPage.consumptionField.label"),
			),
			div(
				{ class: "field is-grouped" },
				div(
					{ class: "control" },
					button(
						{
							class: "button is-primary",
							onclick: () => this.#resetPowerConsumption,
						},
						t("sp2SettingsPage.consumptionField.resetButton"),
					),
				),
				div(
					{ class: "control" },
					input({
						class: "input is-static",
						value: () => `${this.#reactive.sp2.Consumption} Wh`,
					}),
				),
			),
		);

	#ctx: RouteContext;

	#deviceBeepField: ChildDom = () =>
		div(
			{ class: "field" },
			label({ class: "label" }, t("sp2SettingsPage.deviceBeepLabel")),
			div(
				{ class: "control" },
				onOffSelect({
					isOn: () => this.#reactive.sp2.TargetConfig.Beep,
					setOn: (on: boolean) =>
						(this.#reactive.sp2.TargetConfig.Beep = on),
				}),
			),
		);

	#fotaField: ChildDom = () =>
		div(
			{ class: "field" },
			label({ class: "label" }, t("configureSp2Page.fotaLabel")),
			div(
				{ class: "control" },
				button(
					{
						class: "button is-primary",
						disabled: !this.#reactive.sp2.Fota.Filename,
						onclick: () => this.#approveFota(),
					},
					() =>
						this.#reactive.sp2.Fota.Filename
							? t("configureSp2Page.fotaButton.update")()
							: t("configureSp2Page.fotaButton.latestVersion")(),
				),
			),
		);

	#powerFailureField: ChildDom = () =>
		div(
			{ class: "field" },
			label({ class: "label" }, t("sp2SettingsPage.powerFailureLabel")),
			div(
				{ class: "control" },
				onOffSelect({
					isOn: () =>
						this.#reactive.sp2.PowerFailureNotification.Enabled,
					setOn: (on: boolean) =>
						(this.#reactive.sp2.PowerFailureNotification.Enabled =
							on),
				}),
			),
		);

	#powerLoadField: ChildDom = () =>
		div(
			{ class: "field" },
			label({ class: "label" }, t("sp2SettingsPage.powerLoadLabel")),
			div(
				{ class: "field is-grouped" },
				div(
					{ class: "control is-expanded" },
					onOffSelect({
						isOn: () =>
							this.#reactive.sp2.TargetConfig.LoadAlarm.Enabled,
						setOn: (on: boolean) =>
							(this.#reactive.sp2.TargetConfig.LoadAlarm.Enabled =
								on),
					}),
				),
				div(
					{
						class: () =>
							this.#reactive.sp2.TargetConfig.LoadAlarm.Enabled
								? "field has-addons"
								: "is-hidden",
					},
					div(
						{ class: "control is-expanded" },
						input({
							class: "input",
							oninput: (e: Event) => {
								if (!(e.target instanceof HTMLInputElement))
									return;
								this.#reactive.sp2.TargetConfig.LoadAlarm.Lower =
									+e.target.value;
							},
							value: () =>
								this.#reactive.sp2.TargetConfig.LoadAlarm.Lower,
						}),
					),
					div(
						{ class: "control" },
						button({ class: "button is-static" }, "–"),
					),
					div(
						{ class: "control is-expanded" },
						input({
							class: "input",
							oninput: (e: Event) => {
								if (!(e.target instanceof HTMLInputElement))
									return;
								this.#reactive.sp2.TargetConfig.LoadAlarm.Upper =
									+e.target.value;
							},
							value: () =>
								this.#reactive.sp2.TargetConfig.LoadAlarm.Upper,
						}),
					),
					div(
						{ class: "control" },
						button({ class: "button is-static" }, "W"),
					),
				),
			),
		);

	#reactive = reactive({
		activeUser: userPlaceholder,
		hasActiveSubscription: false,
		helpText: "",
		isOwner: false,
		prices: { "": new PriceWrapper("") } as Record<string, PriceWrapper>,
		signedInUser: userPlaceholder,
		sp2: sp2Placeholder,
	});

	#timerField: ChildDom = () =>
		div(
			{ class: "field" },
			label({ class: "label" }, t("sp2SettingsPage.timerField.label")),
			div(
				{ class: "field is-grouped" },
				div(
					{ class: "control" },
					onOffSelect({
						isOn: () => this.#reactive.sp2.Timer.Enabled,
						setOn: (on: boolean) =>
							(this.#reactive.sp2.Timer.Enabled = on),
					}),
				),
				div(
					{
						class: () =>
							this.#reactive.sp2.Timer.Enabled
								? "control"
								: "is-hidden",
					},
					button(
						{
							class: "button is-primary",
							onclick: () => this.#openCreateTimerRuleModal(),
						},
						t("sp2SettingsPage.timerField.createRuleButton"),
					),
				),
			),
			table(
				{
					class: () =>
						this.#reactive.sp2.Timer.Enabled
							? "table"
							: "is-hidden",
				},
				() =>
					tbody(
						this.#reactive.sp2.Timer.Rules.map(
							this.#timerRuleTableRow,
						),
					),
			),
		);

	#timerRuleTableRow = (rule: TimerRule, i: number): ChildDom => {
		const [minutes, hours, dow] = rule.Schedule.split(" ");
		const removeRule = (): void => {
			this.#reactive.sp2.Timer.Rules =
				this.#reactive.sp2.Timer.Rules.slice(0, i).concat(
					this.#reactive.sp2.Timer.Rules.slice(i + 1),
				);
		};
		return tr(
			td(`${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}`),
			td(
				dow
					.split(",")
					.map((i) => (i === "0" ? 7 : +i))
					.sort()
					.map((i) => locale.weekdays()[i % 7].substring(0, 2))
					.join(" "),
			),
			td(
				rule.Action === "turn_on"
					? t("sp2SettingsPage.timerField.ruleTableActions.turnOn")
					: t("sp2SettingsPage.timerField.ruleTableActions.turnOff"),
			),
			td(
				materialSymbol({
					id: "close",
					onclick: removeRule,
				}),
			),
		);
	};

	ChildDom: ChildDom;

	constructor(ctx: RouteContext) {
		this.#ctx = ctx;
		this.ChildDom = deviceSettingsBox({
			ctx: this.#ctx,
			deviceIdType: "IMEI",
			deviceType: "SmartPlug2",
			fields: [
				this.#consumptionField,
				this.#deviceBeepField,
				this.#powerFailureField,
				this.#powerLoadField,
				this.#timerField,
				this.#fotaField,
			],
			getAccess: () => this.#reactive.sp2.Metadata.Access,
			getDeviceId: () => this.#reactive.sp2.Imei,
			getHelpText: () => this.#reactive.helpText,
			getName: () => this.#reactive.sp2.Metadata.Name,
			getNotify: () => this.#reactive.sp2.Metadata.Notify,
			getOwner: () => this.#reactive.sp2.Metadata.OwnerEmail,
			getPriceId: () => this.#reactive.sp2.Subscription.PriceIds.Next,
			getPrices: () => this.#reactive.prices,
			getSignedInUser: () => this.#reactive.signedInUser,
			getTags: () => this.#reactive.sp2.Metadata.Tags,
			isOwner: () => this.#reactive.isOwner,
			save: () => this.#save(),
			setAccess: (emails: string[]) =>
				(this.#reactive.sp2.Metadata.Access = emails),
			setName: (name: string) =>
				(this.#reactive.sp2.Metadata.Name = name),
			setNotify: (emails: string[]) =>
				(this.#reactive.sp2.Metadata.Notify = emails),
			setOwner: (email: string) =>
				(this.#reactive.sp2.Metadata.OwnerEmail = email),
			setPriceId: (priceId: string) =>
				(this.#reactive.sp2.Subscription.PriceIds.Next = priceId),
			setTags: (tags: string[]) =>
				(this.#reactive.sp2.Metadata.Tags = tags),
			unregister: () => this.#unregister(),
		});
	}
}
